/**
 * Animate a small "flier" pill from a source DOM rect to the navbar
 * notification bell so the user has a visual handoff: "your job is now being
 * tracked over there." Used when queuing recipe / variant generation jobs in
 * place of the old full-page redirect.
 *
 * Falls back silently when the bell isn't in the DOM (e.g. logged-out viewer)
 * or when the call happens server-side.
 */

export function flyToBell(originRect: DOMRect, label: string): void {
  if (typeof document === 'undefined') return;

  const bell = document.querySelector<HTMLElement>('[data-notification-bell]');
  if (!bell) return;

  const targetRect = bell.getBoundingClientRect();

  // Respect prefers-reduced-motion — skip the animation but keep the contract
  // the caller relies on.
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reduced) return;

  const flier = document.createElement('div');
  flier.textContent = label;
  flier.setAttribute('aria-hidden', 'true');

  const startX = originRect.left + originRect.width / 2;
  const startY = originRect.top + originRect.height / 2;
  const endX = targetRect.left + targetRect.width / 2;
  const endY = targetRect.top + targetRect.height / 2;

  Object.assign(flier.style, {
    position: 'fixed',
    left: `${startX}px`,
    top: `${startY}px`,
    transform: 'translate(-50%, -50%) scale(1)',
    padding: '4px 10px',
    borderRadius: '999px',
    background: 'var(--c-accent)',
    color: 'var(--c-ink-inverse, #fff)',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.02em',
    pointerEvents: 'none',
    zIndex: '9999',
    transition:
      'left 0.7s cubic-bezier(0.4, 0, 0.2, 1), top 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.7s ease',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
  });

  document.body.appendChild(flier);

  // Force layout so the transition has a starting state to interpolate from.
  void flier.offsetHeight;

  flier.style.left = `${endX}px`;
  flier.style.top = `${endY}px`;
  flier.style.transform = 'translate(-50%, -50%) scale(0.3)';
  flier.style.opacity = '0.2';

  // Pulse the bell when the flier "lands" so the destination feels alive.
  window.setTimeout(() => {
    bell.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.18)' },
        { transform: 'scale(1)' },
      ],
      { duration: 350, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
    );
    flier.remove();
  }, 700);
}
